import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import CreatableSelect from 'react-select/creatable';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { showMessage } from 'app/redux/messageSlice';
import {
  updateDraftOrderSubtypeId,
  updateOrderSubtypeId,
} from 'app/features/orders/services/draftOrderService';
import ordersService from 'app/services/OrdersService/OrdersService';

interface OrderSubtype {
  id: string | null;
  name: string | null;
}

interface OrderSubtypeOption {
  value: string | null;
  label: string | null;
}

interface OrderSubtypeSelectorProps {
  value: { id: string | null; name: string | null } | null;
  onChange: (subtype: { id: string | null; name: string | null }) => void;
  draftOrderId?: string;
  orderId?: string;
}

const OrderSubtypeSelector: FC<OrderSubtypeSelectorProps> = ({
  value,
  onChange,
  draftOrderId,
  orderId,
}) => {
  const dispatch = useDispatch();
  const [subtypesData, setSubtypesData] = useState<OrderSubtype[]>([]);
  const [subtypesLoading, setSubtypesLoading] = useState(true);
  const [subtypesError, setSubtypesError] = useState<any>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const options = useMemo(() => {
    return subtypesData.map((subtype: OrderSubtype) => ({
      value: subtype.id,
      label: subtype.name,
    }));
  }, [subtypesData]);

  const currentValue = useMemo(() => {
    if (!value) return null;
    const matchingOption = options.find((option) => option.value === value.id);
    if (matchingOption) return matchingOption;
    return {
      value: value.id,
      label: value.name,
    };
  }, [value, options]);

  useEffect(() => {
    const loadSubtypes = async () => {
      try {
        setSubtypesLoading(true);
        const data = await ordersService.getAllOrderSubtypes();
        setSubtypesData(data);
      } catch (error) {
        setSubtypesError(error);
      } finally {
        setSubtypesLoading(false);
      }
    };
    loadSubtypes();
  }, []);

  const updateSubtypeAndCallAPI = useCallback(
    async (selectedOption: OrderSubtypeOption | null) => {
      if (!draftOrderId && !orderId) {
        return;
      }
      if (draftOrderId) {
        await updateDraftOrderSubtypeId(draftOrderId, selectedOption?.value || null);
      } else if (orderId) {
        await updateOrderSubtypeId(orderId, selectedOption?.value || null);
      }
    },
    [draftOrderId, orderId]
  );

  const handleUpdateSubtype = useCallback(
    debounce(async (selectedOption: OrderSubtypeOption | null) => {
      // Check if the value is actually changing
      if (
        (selectedOption?.value === value?.id && selectedOption?.label === value?.name) ||
        (!selectedOption && !value)
      ) {
        return;
      }

      // Prepare the new value
      const newValue = selectedOption
        ? { id: selectedOption.value, name: selectedOption.label }
        : { id: null, name: null };

      // Update local state first
      onChange(newValue);

      // Only call API if we have an order/draft ID
      if (draftOrderId || orderId) {
        try {
          setIsUpdating(true);
          await updateSubtypeAndCallAPI(selectedOption);
          
          dispatch(
            showMessage({
              text: `Successfully updated order subtype`,
              variant: 'success',
            })
          );
        } catch (error) {
          console.error('Error updating order subtype:', error);
          dispatch(
            showMessage({
              text: 'Error occurred while updating order subtype',
              variant: 'error',
            })
          );
          // Revert to previous value on error
          onChange(value || { id: null, name: null });
        } finally {
          setIsUpdating(false);
        }
      }
    }, 300),
    [value, onChange, draftOrderId, orderId, dispatch, updateSubtypeAndCallAPI]
  );

  const normalizeString = (str: string) => str.trim().toLowerCase();

  const isValidNewOption = (inputValue: string) => {
    const normalizedInput = normalizeString(inputValue);
    const isExisting = subtypesData.some(
      (subtype) => normalizeString(subtype.name || '') === normalizedInput
    );
    return !isExisting && inputValue.trim().length > 0 && inputValue.length <= 50;
  };

  const handleCreateSubtype = async (inputValue: string) => {
    const normalizedInput = normalizeString(inputValue);
    const trimmedInput = inputValue.trim();
    const isExisting = subtypesData.some(
      (subtype) => normalizeString(subtype.name || '') === normalizedInput
    );

    if (isExisting) {
      dispatch(
        showMessage({
          text: `"${trimmedInput}" already exists as an order subtype`,
          variant: 'warning',
        })
      );
      return;
    }

    if (trimmedInput.length === 0) {
      dispatch(
        showMessage({
          text: 'Order subtype cannot be empty',
          variant: 'error',
        })
      );
      return;
    }

    if (inputValue.length > 50) {
      dispatch(
        showMessage({
          text: 'Order subtype name must be 50 characters or less',
          variant: 'error',
        })
      );
      return;
    }

    try {
      setIsUpdating(true);
      const newSubtype = await ordersService.createOrderSubtype(inputValue);

      if (newSubtype) {
        setSubtypesData((prev) => [newSubtype, ...prev]);
        const newOption: OrderSubtypeOption = {
          value: newSubtype.id,
          label: newSubtype.name,
        };
        await updateSubtypeAndCallAPI(newOption);

        onChange({
          id: newOption.value,
          name: newOption.label,
        });

        dispatch(
          showMessage({
            text: `Order subtype "${newSubtype.name}" created and assigned successfully`,
            variant: 'success',
          })
        );
      }
    } catch (error) {
      console.error('Error creating order subtype:', error);
      dispatch(
        showMessage({
          text: 'Error occurred while creating order subtype',
          variant: 'error',
        })
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className='order-subtype-selector' style={{ margin: '8px 0' }}>
      <label className='order-subtype-label fw-bolder fs-6 mb-1 d-block'>Order Subtype</label>
      <CreatableSelect
        isClearable
        isDisabled={subtypesLoading || isUpdating}
        isLoading={subtypesLoading || isUpdating}
        value={currentValue}
        options={options}
        onChange={handleUpdateSubtype}
        onCreateOption={handleCreateSubtype}
        isValidNewOption={isValidNewOption}
        placeholder='Select or create order subtype...'
        noOptionsMessage={() =>
          subtypesError
            ? 'Error loading subtypes'
            : 'No subtypes available. Type to create a new one.'
        }
        loadingMessage={() => 'Loading subtypes...'}
        createOptionPosition='last'
        formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
        className='basic-single'
        classNamePrefix='select'
        menuPortalTarget={document.body}
        styles={{
          control: (base) => ({
            ...base,
            minHeight: '32px',
            fontSize: '14px',
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
          option: (base) => ({
            ...base,
            fontSize: '14px',
            padding: '6px 12px',
          }),
          singleValue: (base) => ({
            ...base,
            fontSize: '14px',
          }),
          input: (base) => ({
            ...base,
            fontSize: '14px',
          }),
        }}
      />
    </div>
  );
};

export default OrderSubtypeSelector;